import React, { useMemo, useState } from "react";
import "./RsvpComponent.scss";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import { db } from "../firebase/firebase";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { collection, getDocs } from "firebase/firestore";
import rsvpTop from "./rsvp.png";
import { createPortal } from "react-dom";

const GROOM_COLLECTION = "GroomGuests";
const BRIDE_COLLECTION = "BrideGuests";

const RsvpComponent = ({ slug = null, weddingData = null }) => {
  const TOTAL_STEPS = 3;
  const [step, setStep] = useState(1);

  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  const [results, setResults] = useState([]);
  const [responses, setResponses] = useState({});
  const [rowLoadingId, setRowLoadingId] = useState(null);

  const [modal, setModal] = useState({ open: false, message: "" });

  const ceremonyTypeRaw = weddingData?.ceremony_type || "wedding";
  const isHomeComing = useMemo(() => {
    const normalized = String(ceremonyTypeRaw).trim().toLowerCase();
    return (
      normalized === "home coming" ||
      normalized === "homecoming" ||
      normalized === "home-coming"
    );
  }, [ceremonyTypeRaw]);

  const sectionEyebrow = isHomeComing ? "Homecoming RSVP" : "Wedding RSVP";
  const mediaTitle = isHomeComing
    ? "A homecoming celebration we would love to share with you"
    : "A celebration we would love to share with you";
  const introKicker = "Please confirm your attendance";
  const introHeadline = "We are so happy to invite you";
  const introSubline =
    "Search using your family name and send your RSVP in just a few seconds.";
  const siteNotReadyMessage = isHomeComing
    ? "Homecoming site is not ready yet."
    : "Wedding site is not ready yet.";
  const collectionsNotReadyMessage = isHomeComing
    ? "Homecoming guest collections are not ready yet."
    : "Wedding guest collections are not ready yet.";
  const searchButtonText = searchLoading ? "Searching…" : "Search Guest List";
  const thankYouLine = "Your RSVP response has been recorded successfully.";

  const rsvpImage = useMemo(() => {
    const imageGallery = weddingData?.imageGallery;

    if (
      imageGallery &&
      typeof imageGallery === "object" &&
      !Array.isArray(imageGallery)
    ) {
      return imageGallery?.rsvpSectionImage || rsvpTop;
    }

    if (Array.isArray(imageGallery)) {
      return imageGallery?.[1]?.image || rsvpTop;
    }

    return rsvpTop;
  }, [weddingData]);

  const normalizeFamilyName = (value) => {
    return String(value || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "")
      .replace(/['’`".,()-]/g, "");
  };

  const groomCollectionName = GROOM_COLLECTION;
  const brideCollectionName = BRIDE_COLLECTION;

  const progress = useMemo(() => (step / TOTAL_STEPS) * 100, [step]);

  const handleSearch = async (e) => {
    e.preventDefault();

    const raw = query.trim();

    if (!raw) {
      setModal({
        open: true,
        message: "Family name is required. Please enter a family name to search.",
      });
      return;
    }

    if (!slug) {
      setModal({
        open: true,
        message: siteNotReadyMessage,
      });
      return;
    }

    if (!groomCollectionName || !brideCollectionName) {
      setModal({
        open: true,
        message: collectionsNotReadyMessage,
      });
      return;
    }

    setSubmittedQuery(raw);
    setSearchLoading(true);

    try {
      const normalizedSearch = normalizeFamilyName(raw);

      const groomRef = collection(db, "sites", slug, groomCollectionName);
      const brideRef = collection(db, "sites", slug, brideCollectionName);

      const [groomSnap, brideSnap] = await Promise.all([
        getDocs(groomRef),
        getDocs(brideRef),
      ]);

      const matchedGuests = [];

      const collectMatches = (snapshot, collectionName) => {
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const familyName = data.familyName || "";
          const guests = Array.isArray(data.guests) ? data.guests : [];

          if (normalizeFamilyName(familyName) !== normalizedSearch) {
            return;
          }

          guests.forEach((guest, index) => {
            if (!guest?.name) return;

            matchedGuests.push({
              id: guest.id || `${docSnap.id}-${index}`,
              guestIndex: index,
              name: guest.name,
              documentId: docSnap.id,
              collectionName,
              status: guest.status || "Pending",
              familyName,
            });
          });
        });
      };

      collectMatches(groomSnap, groomCollectionName);
      collectMatches(brideSnap, brideCollectionName);

      setResults(matchedGuests);
      setResponses({});
      setStep(2);
    } catch (err) {
      console.error("Error fetching guests:", err);
      setResults([]);
      setResponses({});
      setStep(2);
    } finally {
      setSearchLoading(false);
    }
  };

  const updateGuestStatus = async (guest, newStatusLabel) => {
    if (!slug) return false;

    try {
      const app = getApp();
      const functions = getFunctions(app, "us-central1");
      const submitPublicRsvp = httpsCallable(functions, "submitPublicRsvp");

      const result = await submitPublicRsvp({
        slug,
        collectionName: guest.collectionName,
        documentId: guest.documentId,
        guestIndex: guest.guestIndex,
        newStatus: newStatusLabel,
      });

      console.log("submitPublicRsvp success:", result.data);
      return true;
    } catch (err) {
      console.error("submitPublicRsvp error object:", err);
      console.error("code:", err?.code);
      console.error("message:", err?.message);
      console.error("details:", err?.details);

      setModal({
        open: true,
        message:
          err?.message ||
          err?.details ||
          err?.code ||
          "RSVP function failed.",
      });

      return false;
    }
  };

  const hasResponded = (guest, picked) =>
    !!picked ||
    guest.status === "Participation Confirmed" ||
    guest.status === "Not Participating";

  const onComing = async (guest) => {
    if (
      responses[guest.id] ||
      guest.status === "Participation Confirmed" ||
      guest.status === "Not Participating" ||
      rowLoadingId !== null
    ) {
      return;
    }

    setRowLoadingId(guest.id);
    const ok = await updateGuestStatus(guest, "Participation Confirmed");
    setRowLoadingId(null);

    if (ok) {
      setResponses((prev) => ({ ...prev, [guest.id]: "coming" }));
      setResults((prev) =>
        prev.map((r) =>
          r.id === guest.id ? { ...r, status: "Participation Confirmed" } : r
        )
      );
      setModal({ open: true, message: "Your RSVP has been updated successfully." });
    } else {
      setModal({
        open: true,
        message: "Something went wrong saving your RSVP. Please try again.",
      });
    }
  };

  const onNotComing = async (guest) => {
    if (
      responses[guest.id] ||
      guest.status === "Participation Confirmed" ||
      guest.status === "Not Participating" ||
      rowLoadingId !== null
    ) {
      return;
    }

    setRowLoadingId(guest.id);
    const ok = await updateGuestStatus(guest, "Not Participating");
    setRowLoadingId(null);

    if (ok) {
      setResponses((prev) => ({ ...prev, [guest.id]: "not" }));
      setResults((prev) =>
        prev.map((r) =>
          r.id === guest.id ? { ...r, status: "Not Participating" } : r
        )
      );
      setModal({ open: true, message: "Your RSVP has been updated successfully." });
    } else {
      setModal({
        open: true,
        message: "Something went wrong saving your RSVP. Please try again.",
      });
    }
  };

  const closeModal = () => setModal({ open: false, message: "" });

  const resetRsvpFlow = () => {
    setStep(1);
    setQuery("");
    setSubmittedQuery("");
    setResults([]);
    setResponses({});
    setRowLoadingId(null);
    setSearchLoading(false);
    setModal({ open: false, message: "" });
  };

  const goBack = () => {
    setStep(1);
    setResults([]);
    setResponses({});
    setSubmittedQuery("");
  };

  const completeRsvp = () => setStep(3);

  return (
    <section
      className={`rsvpFlow ${modal.open ? "rsvpFlow--blurred" : ""}`}
      aria-label="RSVP Flow"
    >
      <div className="rsvpFlow__card">
        <div className="rsvpFlow__progress" aria-hidden="true">
          <div
            className="rsvpFlow__progressFill"
            style={{ width: `${progress}%` }}
          />
        </div>

        {(step === 1 || step === 2) && (
          <div className="rsvpFlow__split rsvpFlow__step rsvpFlow__step--enter">
            <figure className="rsvpFlow__media">
              <img
                src={rsvpImage}
                alt={isHomeComing ? "Homecoming RSVP" : "Wedding RSVP"}
                loading="lazy"
              />
              <div className="rsvpFlow__mediaOverlay">
                <p className="rsvpFlow__eyebrow">{sectionEyebrow}</p>
                <h2 className="rsvpFlow__mediaTitle">{mediaTitle}</h2>
              </div>
            </figure>

            <div className="rsvpFlow__panel">
              <div className="rsvpFlow__intro">
                <p className="rsvpFlow__kicker">{introKicker}</p>
                <h2 className="rsvpFlow__headline">{introHeadline}</h2>
                <p
                  className="rsvpFlow__subline"
                  style={{ width: "100%", maxWidth: "100%" }}
                >
                  {introSubline}
                </p>
              </div>

              <form
                className="rsvpFlow__search"
                onSubmit={handleSearch}
                role="search"
              >
                <label className="rsvpFlow__label" htmlFor="family-search">
                  Family name
                </label>
                <input
                  id="family-search"
                  type="text"
                  className="rsvpFlow__input"
                  placeholder="Enter your family name"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Search guests"
                  aria-required="true"
                  disabled={searchLoading}
                  autoCapitalize="words"
                  autoCorrect="off"
                />

                <ButtonComponent type="submit" disabled={searchLoading}>
                  {searchButtonText}
                </ButtonComponent>
              </form>

              {step === 2 && (
                <>
                  <div className="rsvpFlow__results" role="region" aria-live="polite">
                    <div className="rsvpFlow__resultsHead">
                      <div className="rsvpFlow__count">
                        Found <strong>{results.length}</strong> guest
                        {results.length !== 1 ? "s" : ""}
                      </div>
                      <div className="rsvpFlow__keyword">
                        Family Name — <strong>{submittedQuery || "—"}</strong>
                      </div>
                    </div>

                    <ul className="rsvpFlow__list">
                      {results.map((guest) => {
                        const picked = responses[guest.id];
                        const responded = hasResponded(guest, picked);
                        const isLoading = rowLoadingId === guest.id;

                        const isComing =
                          picked === "coming" ||
                          guest.status === "Participation Confirmed";

                        const respondedLabel = isComing
                          ? "Responded: Coming"
                          : "Responded: Not Coming";

                        return (
                          <li
                            key={`${guest.collectionName}-${guest.documentId}-${guest.id}`}
                            className={`rsvpFlow__row ${isLoading ? "is-loading" : ""
                              }`}
                          >
                            <div className="rsvpFlow__guestBlock">
                              <span className="rsvpFlow__guestLabel">Guest</span>
                              <span className="rsvpFlow__name">{guest.name}</span>
                            </div>

                            {responded ? (
                              <span
                                className={`rsvpFlow__status ${isComing ? "is-coming" : "is-not"
                                  }`}
                              >
                                {respondedLabel}
                              </span>
                            ) : isLoading ? (
                              <span className="rsvpFlow__actions">Saving…</span>
                            ) : (
                              <span className="rsvpFlow__actions">
                                <ButtonComponent onClick={() => onComing(guest)}>
                                  I’m Coming
                                </ButtonComponent>
                                <ButtonComponent
                                  className="is-ghost"
                                  onClick={() => onNotComing(guest)}
                                >
                                  Not Coming
                                </ButtonComponent>
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>

                    {!searchLoading && results.length === 0 && (
                      <div className="rsvpFlow__empty">
                        <h3 className="rsvpFlow__emptyTitle">No guests found</h3>
                        <p className="rsvpFlow__emptyText">
                          Please check the family name and try again.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="rsvpFlow__footer rsvpFlow__footer--split">
                    <ButtonComponent className="is-ghost" onClick={goBack}>
                      Back
                    </ButtonComponent>
                    <ButtonComponent onClick={completeRsvp}>
                      Complete RSVP
                    </ButtonComponent>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="rsvpFlow__split rsvpFlow__step rsvpFlow__step--enter">
            <figure className="rsvpFlow__media rsvpFlow__media--thankyou">
              <img src={rsvpImage} alt="RSVP completed" loading="lazy" />
            </figure>

            <div className="rsvpFlow__panel rsvpFlow__panel--center">
              <p className="rsvpFlow__kicker">RSVP Completed</p>
              <h3 className="rsvpFlow__thanksTitle">Thank you!</h3>
              <p className="rsvpFlow__thanksLine">{thankYouLine}</p>

              <div className="rsvpFlow__footerSingle">
                <ButtonComponent as="button" onClick={resetRsvpFlow}>
                  RSVP Again
                </ButtonComponent>
              </div>
            </div>
          </div>
        )}
      </div>

      {modal.open &&
        createPortal(
          <div className="rsvpFlowModal" role="dialog" aria-modal="true">
            <div className="rsvpFlowModal__panel">
              <p className="rsvpFlowModal__message">{modal.message}</p>
              <ButtonComponent type="button" onClick={closeModal}>
                Close
              </ButtonComponent>
            </div>
          </div>,
          document.body
        )}
    </section>
  );
};

export default RsvpComponent;